/**
 * F-0793 (`docs/concept-page-layout.md`): a list-and-detail block whose page lets readers resize its
 * border (`config.readerResize`), and the width each reader chose there, kept with the person
 * (`users.preferences.pageColumnWidths`) so it is the same after a reload and on every device. The
 * page itself never stores a reader's width.
 *
 * A width is one column's, in CSS pixels, of a `kind`: `details`, the details column measured from
 * the block's right edge (`ResizableDetailPanel`), or `list`, the list column measured from its left
 * (`WaitingSplit`). Each kind has its own bounds. Absent means the page's own width.
 *
 * `pageId` names a page record, or an app route with no record by a fixed id (`app:tasks-waiting`):
 * a width is stored without looking the page up, so the write says nothing about which pages exist.
 */

/**
 * The block types that draw a list beside an item's details, the only ones with that border: the
 * tasks block, a next action across what the reader may read (`scope: 'readable'`, Your tasks), and
 * an item list whose items open beside it (`detail: 'pane'`, PX-43).
 */
export const READER_RESIZABLE_BLOCK_TYPES: readonly string[] = [
  'tasks',
  'next-action',
  'work-item-list',
];

export function mayLetReadersResize(
  blockType: string,
  config?: { readonly detail?: unknown; readonly scope?: unknown } | null,
): boolean {
  if (!READER_RESIZABLE_BLOCK_TYPES.includes(blockType)) return false;
  if (blockType === 'next-action') return config?.scope === 'readable';
  if (blockType === 'work-item-list') return config?.detail === 'pane';
  return true;
}

/** Why a block's `readerResize` is refused: a sentence, as a page save answers it. */
export const READER_RESIZE_REFUSED =
  'Readers can resize only a block that shows a list beside an item’s details: Tasks, Your tasks (a next action across what the reader may read), or an item list whose items open beside it.';

/**
 * The narrowest and widest the details column may be, the resize handle's own bounds: neither the
 * list nor the details collapse.
 */
export const PAGE_DETAIL_WIDTH_MIN = 360;
export const PAGE_DETAIL_WIDTH_MAX = 760;
/** The details column's width until the reader chooses one. */
export const PAGE_DETAIL_WIDTH_DEFAULT = 480;
/** How many widths one person keeps; the least recently set go first. */
export const PAGE_COLUMN_WIDTHS_MAX_ENTRIES = 200;

/** The list column's bounds, where the border is measured on the list (`WaitingSplit`). */
export const PAGE_LIST_WIDTH_MIN = 256;
export const PAGE_LIST_WIDTH_MAX = 640;

/** Which column a width is: the details column's (the default) or the list column's. */
export const PAGE_COLUMN_WIDTH_KINDS = ['details', 'list'] as const;
export type PageColumnWidthKindType = (typeof PAGE_COLUMN_WIDTH_KINDS)[number];

export function clampPageColumnWidth(
  width: number,
  kind: PageColumnWidthKindType = 'details',
): number {
  const [min, max] =
    kind === 'list'
      ? [PAGE_LIST_WIDTH_MIN, PAGE_LIST_WIDTH_MAX]
      : [PAGE_DETAIL_WIDTH_MIN, PAGE_DETAIL_WIDTH_MAX];
  return Math.min(max, Math.max(min, Math.round(width)));
}

const kindOf = (value: unknown): PageColumnWidthKindType => (value === 'list' ? 'list' : 'details');

/** One reader's chosen width on one block of one page. */
export interface IPageColumnWidth {
  readonly pageId: string;
  readonly blockId: string;
  readonly kind: PageColumnWidthKindType;
  readonly width: number;
  readonly setAt: string;
}

export interface IPageColumnWidthsPreferences {
  readonly widths: readonly IPageColumnWidth[];
}

/** A write: the width chosen, or `null` to go back to the page's own. */
export interface IPageColumnWidthChange {
  readonly pageId: string;
  readonly blockId: string;
  readonly kind: PageColumnWidthKindType;
  readonly width: number | null;
}

const isText = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max;

/** What is stored, read leniently: an entry that is not a width on a page's block is dropped. */
export function readPageColumnWidths(stored: unknown): IPageColumnWidthsPreferences {
  const list = (stored as { widths?: unknown } | null | undefined)?.widths;
  if (!Array.isArray(list)) return { widths: [] };
  const widths: IPageColumnWidth[] = [];
  for (const entry of list) {
    const row = entry as Record<string, unknown> | null;
    if (
      !row ||
      !isText(row.pageId, 80) ||
      !isText(row.blockId, 120) ||
      typeof row.width !== 'number' ||
      !Number.isFinite(row.width)
    ) {
      continue;
    }
    const kind = kindOf(row.kind);
    widths.push({
      pageId: row.pageId,
      blockId: row.blockId,
      kind,
      width: clampPageColumnWidth(row.width, kind),
      setAt: typeof row.setAt === 'string' ? row.setAt : '',
    });
  }
  return { widths: widths.slice(-PAGE_COLUMN_WIDTHS_MAX_ENTRIES) };
}

/** What a reader reads: their width of each border, by page and block (`{ [pageId]: { [blockId]: px } }`). */
export type PageColumnWidthsMapType = Record<string, Record<string, number>>;

/** The kept widths as the reader reads them, by page and then block. */
export function pageColumnWidthsMap(widths: IPageColumnWidthsPreferences): PageColumnWidthsMapType {
  const map: PageColumnWidthsMapType = {};
  for (const entry of widths.widths) (map[entry.pageId] ??= {})[entry.blockId] = entry.width;
  return map;
}

/** The change, checked, or the sentence that says what is wrong with it. */
export function parsePageColumnWidthChange(
  input: unknown,
): { ok: true; change: IPageColumnWidthChange } | { ok: false; error: string } {
  const body = (input ?? {}) as Record<string, unknown>;
  if (!isText(body.pageId, 80)) return { ok: false, error: 'pageId must name a page.' };
  if (!isText(body.blockId, 120)) return { ok: false, error: 'blockId must name a block.' };
  if (body.kind !== undefined && !PAGE_COLUMN_WIDTH_KINDS.includes(body.kind as never)) {
    return { ok: false, error: 'kind must be details or list.' };
  }
  const kind = kindOf(body.kind);
  if (body.width === null) {
    return { ok: true, change: { pageId: body.pageId, blockId: body.blockId, kind, width: null } };
  }
  if (typeof body.width !== 'number' || !Number.isFinite(body.width)) {
    return { ok: false, error: 'width must be a number of pixels, or null for the page’s own.' };
  }
  return {
    ok: true,
    change: {
      pageId: body.pageId,
      blockId: body.blockId,
      kind,
      width: clampPageColumnWidth(body.width, kind),
    },
  };
}

/**
 * The widths after one change: the block's entry replaced and moved last (most recently set), or
 * removed for `null`; the oldest dropped past {@link PAGE_COLUMN_WIDTHS_MAX_ENTRIES}.
 */
export function withPageColumnWidth(
  current: IPageColumnWidthsPreferences,
  change: IPageColumnWidthChange,
  at: string,
): IPageColumnWidthsPreferences {
  const others = current.widths.filter(
    (entry) => !(entry.pageId === change.pageId && entry.blockId === change.blockId),
  );
  if (change.width === null) return { widths: others };
  const next = [
    ...others,
    {
      pageId: change.pageId,
      blockId: change.blockId,
      kind: change.kind,
      width: change.width,
      setAt: at,
    },
  ];
  return { widths: next.slice(-PAGE_COLUMN_WIDTHS_MAX_ENTRIES) };
}
